import React, {
  useState,
  useMemo,
  useRef,
  useEffect,
  useCallback,
} from "react";
import {
  Box,
  Container,
  Typography,
  Button,
  useTheme,
  alpha,
  Drawer,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Card,
  CardContent,
  Chip,
  IconButton,
  Dialog,
  Skeleton,
  DialogTitle,
  DialogContent,
  DialogActions,
  Paper,
  LinearProgress,
  Checkbox,
  FormControlLabel,
  Tooltip,
  Autocomplete,
} from "@mui/material";
import {
  Add as AddIcon,
  Close as CloseIcon,
  Payment as PaymentIcon,
  Person as PersonIcon,
  AttachMoney as MoneyIcon,
  Phone as PhoneIcon,
  Email as EmailIcon,
  History as HistoryIcon,
  CheckCircle as CheckCircleIcon,
  Pending as PendingIcon,
  CalendarToday as CalendarIcon,
  Warning as WarningIcon,
  CreditCard as CreditCardIcon,
  Search as SearchIcon,
} from "@mui/icons-material";
import { AgGridReact } from "ag-grid-react";
import VisibilityIcon from "@mui/icons-material/Visibility";
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-alpine.css";
import "ag-grid-community/styles/ag-theme-material.css";
import { DataContext } from "../../context/DataContext";

// Stats Cards Skeleton
const StatsSkeleton = () => (
  <Grid container spacing={3} sx={{ mb: 4 }}>
    {[1, 2, 3].map((item) => (
      <Grid item xs={12} sm={6} md={3} key={item}>
        <Card sx={{ borderRadius: "20px", backgroundColor: "transparent" }}>
          <CardContent>
            <Skeleton variant="text" width={100} height={20} />
            <Skeleton variant="text" width={80} height={40} sx={{ mt: 1 }} />
          </CardContent>
        </Card>
      </Grid>
    ))}
  </Grid>
);

// Table Skeleton
const TableSkeleton = () => (
  <Card
    sx={{
      borderRadius: "20px",
      overflow: "hidden",
      backgroundColor: "transparent",
    }}
  >
    <Box sx={{ p: 2 }}>
      {[1, 2, 3, 4, 5].map((item) => (
        <Box
          key={item}
          sx={{ mb: 2, display: "flex", alignItems: "center", gap: 2 }}
        >
          <Skeleton variant="circular" width={40} height={40} />
          <Box sx={{ flex: 1 }}>
            <Skeleton variant="text" width="30%" height={25} />
            <Skeleton variant="text" width="50%" height={20} />
          </Box>
          <Skeleton
            variant="rectangular"
            width={100}
            height={35}
            sx={{ borderRadius: 1 }}
          />
        </Box>
      ))}
    </Box>
  </Card>
);

// Generate months for a scheme
const generateMonths = (startDate, monthlyAmount) => {
  const months = [];
  const start = new Date(startDate);

  for (let i = 0; i < 12; i++) {
    const monthDate = new Date(start);
    monthDate.setMonth(start.getMonth() + i);

    months.push({
      month: i + 1,
      dueDate: monthDate.toISOString().split("T")[0],
      amount: monthlyAmount,
      paidAmount: 0,
      status: "Pending",
      paymentDate: null,
      paymentMode: null,
      staffName: null,
    });
  }

  return months;
};

// Custom Cell Renderers
const StatusCellRenderer = (props) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const status = props.value;

  const getStatusConfig = () => {
    switch (status) {
      case "Active":
        return {
          bg: isDark ? alpha("#3B82F6", 0.2) : alpha("#3B82F6", 0.1),
          color: "#3B82F6",
          icon: <PendingIcon sx={{ fontSize: 14, mr: 0.5 }} />,
        };
      case "Completed":
        return {
          bg: isDark ? alpha("#10B981", 0.2) : alpha("#10B981", 0.1),
          color: "#10B981",
          icon: <CheckCircleIcon sx={{ fontSize: 14, mr: 0.5 }} />,
        };
      default:
        return {
          bg: isDark ? alpha("#64748B", 0.2) : alpha("#64748B", 0.1),
          color: "#64748B",
          icon: null,
        };
    }
  };

  const config = getStatusConfig();

  return (
    <Chip
      icon={config.icon}
      label={status}
      size="small"
      sx={{
        fontWeight: 600,
        fontSize: "0.75rem",
        backgroundColor: config.bg,
        color: config.color,
        borderRadius: "8px",
        border: `1px solid ${alpha(config.color, 0.3)}`,
      }}
    />
  );
};

const CardStatusCellRenderer = (props) => {
  const { cardLostFee } = props.data;

  return (
    <Tooltip
      title={
        cardLostFee > 0
          ? "Card was lost and ₹100 fine paid"
          : "Card issued free"
      }
    >
      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        <CreditCardIcon
          sx={{
            fontSize: 18,
            color: cardLostFee > 0 ? "#F59E0B" : "#10B981",
          }}
        />
        {cardLostFee > 0 && (
          <Chip
            label="Lost Card"
            size="small"
            sx={{
              backgroundColor: alpha("#F59E0B", 0.1),
              color: "#F59E0B",
              border: `1px solid ${alpha("#F59E0B", 0.3)}`,
              fontSize: "0.65rem",
              height: 20,
            }}
          />
        )}
      </Box>
    </Tooltip>
  );
};

const ProgressCellRenderer = (props) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const { monthsCompleted } = props.data;
  const progress = (monthsCompleted / 12) * 100;

  return (
    <Box sx={{ width: "100%" }}>
      <Box sx={{ display: "flex", justifyContent: "space-between", mb: 0.5 }}>
        <Typography variant="caption" sx={{ color: "text.secondary" }}>
          {monthsCompleted}/12 months
        </Typography>
        <Typography
          variant="caption"
          sx={{ fontWeight: 600, color: "#484bec" }}
        >
          {Math.round(progress)}%
        </Typography>
      </Box>
      <LinearProgress
        variant="determinate"
        value={progress}
        sx={{
          height: 6,
          borderRadius: 3,
          backgroundColor: isDark
            ? alpha("#1e293b", 0.8)
            : alpha("#e2e8f0", 0.8),
          "& .MuiLinearProgress-bar": {
            borderRadius: 3,
            background: "linear-gradient(90deg, #484bec 0%, #3730a3 100%)",
          },
        }}
      />
    </Box>
  );
};

const AmountCellRenderer = (props) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const value = props.value;

  return (
    <Typography
      sx={{
        fontWeight: 600,
        fontFamily: "monospace",
        color: isDark ? "#FBBF24" : "#D97706",
      }}
    >
      ₹{value?.toLocaleString() || 0}
    </Typography>
  );
};

const BonusCellRenderer = (props) => {
  const { bonus, monthlyAmount } = props.data;
  let maturityAmount = 13.5 * monthlyAmount;

  return (
    <Box>
      <Typography
        sx={{
          fontWeight: 700,
          fontFamily: "monospace",
          color: "#10B981",
        }}
      >
        ₹{maturityAmount.toLocaleString()}
      </Typography>
      <Typography variant="caption" sx={{ color: "text.secondary" }}>
        ({bonus}x bonus)
      </Typography>
    </Box>
  );
};

const ActionCellRenderer = (props) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const { onViewDetails, onAddTransaction, onReportLostCard } = props.context;

  return (
    <Box sx={{ display: "flex", gap: 1 }}>
      <Button
        size="small"
        variant="outlined"
        startIcon={<VisibilityIcon />}
        onClick={() => onViewDetails(props.data)}
        sx={{
          borderRadius: "8px",
          fontSize: "0.7rem",
          fontWeight: 600,
          textTransform: "none",
          borderColor: alpha("#6366F1", 0.5),
          color: isDark ? "#A5B4FC" : "#4F46E5",
          "&:hover": {
            borderColor: "#6366F1",
            backgroundColor: alpha("#6366F1", 0.08),
          },
        }}
      >
        View
      </Button>
      {props.data.status === "Active" && (
        <>
          <Button
            size="small"
            variant="contained"
            startIcon={<PaymentIcon />}
            onClick={() => onAddTransaction(props.data)}
            sx={{
              borderRadius: "8px",
              fontSize: "0.7rem",
              fontWeight: 600,
              textTransform: "none",
              background: "linear-gradient(135deg, #2563EB, #1D4ED8)",
              boxShadow: "0 4px 10px rgba(72, 148, 236, 0.3)",
              "&:hover": {
                transform: "translateY(-1px)",
                boxShadow: "0 6px 15px rgba(72, 138, 236, 0.4)",
              },
            }}
          >
            Pay
          </Button>
          {!props.data.cardLostFee && (
            <Tooltip title="Report lost card (₹100 fine)">
              <Button
                size="small"
                variant="outlined"
                startIcon={<WarningIcon />}
                onClick={() => onReportLostCard(props.data)}
                sx={{
                  borderRadius: "8px",
                  fontSize: "0.7rem",
                  fontWeight: 600,
                  textTransform: "none",
                  borderColor: alpha("#F59E0B", 0.5),
                  color: "#F59E0B",
                  minWidth: 40,
                  "&:hover": {
                    borderColor: "#F59E0B",
                    backgroundColor: alpha("#F59E0B", 0.08),
                  },
                }}
              >
                Lost
              </Button>
            </Tooltip>
          )}
        </>
      )}
    </Box>
  );
};

// Month Status Chip
const MonthStatusChip = ({ status }) => {
  const getStatusColor = () => {
    switch (status) {
      case "Paid":
        return {
          bg: alpha("#10B981", 0.1),
          color: "#10B981",
          border: alpha("#10B981", 0.3),
        };
      case "Pending":
        return {
          bg: alpha("#F59E0B", 0.1),
          color: "#F59E0B",
          border: alpha("#F59E0B", 0.3),
        };
      default:
        return {
          bg: alpha("#64748B", 0.1),
          color: "#64748B",
          border: alpha("#64748B", 0.3),
        };
    }
  };

  const colors = getStatusColor();

  return (
    <Chip
      label={status}
      size="small"
      sx={{
        fontWeight: 600,
        fontSize: "0.65rem",
        backgroundColor: colors.bg,
        color: colors.color,
        border: `1px solid ${colors.border}`,
        borderRadius: "6px",
        height: 20,
      }}
    />
  );
};

// Terms and Conditions Component
const TermsAndConditions = ({ accepted, onAcceptChange }) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";

  return (
    <Paper
      sx={{
        p: 2,
        backgroundColor: isDark ? alpha("#1e293b", 0.5) : alpha("#f8fafc", 0.8),
        borderRadius: "12px",
        border: `1px solid ${alpha(isDark ? "#475569" : "#e2e8f0", 0.3)}`,
      }}
    >
      <Typography
        variant="subtitle2"
        sx={{ fontWeight: 600, mb: 1, color: "#487cec" }}
      >
        Terms & Conditions
      </Typography>
      <Box sx={{ maxHeight: 200, overflowY: "auto", mb: 2, pr: 1 }}>
        <Typography
          variant="caption"
          component="div"
          sx={{ color: "text.secondary" }}
        >
          <ul style={{ paddingLeft: 20, margin: 0 }}>
            <li>This scheme is for 12 months only.</li>
            <li>
              It is compulsory to deposit the amount every month in the scheme.
            </li>
            <li>
              After completing 12 months, a bonus of 1.5x (as decided by the
              jeweller) will be given to the customer.
            </li>
            <li>The card is issued free of cost at the start of the scheme.</li>
            <li>
              If the card is lost, a fine of ₹100 will be charged for a new
              card.
            </li>
            <li>
              The deposited amount cannot be withdrawn in any situation during
              the scheme period.
            </li>
            <li>
              If the card is closed before 12 months, the accumulated amount
              will be settled after 12 months in the form of gold jewellery.
            </li>
            <li>
              Regarding the price of gold, the final decision of the shop
              counter will be considered valid.
            </li>
            <li>
              Jewellery will be given according to the prevailing market rate of
              gold at that time.
            </li>
            <li>
              This scheme is not applicable to gold biscuits or gold coins.
            </li>
          </ul>
        </Typography>
      </Box>
      <FormControlLabel
        control={
          <Checkbox
            checked={accepted}
            onChange={onAcceptChange}
            sx={{
              color: "#484bec",
              "&.Mui-checked": {
                color: "#484bec",
              },
            }}
          />
        }
        label={
          <Typography variant="body2" sx={{ color: "text.primary" }}>
            I have read and accept the terms and conditions
          </Typography>
        }
      />
    </Paper>
  );
};

// Report Lost Card Dialog
const ReportLostCardDialog = ({
  open,
  onClose,
  onConfirm,
  customerName,
  isDark,
}) => {
  const [staffName, setStaffName] = useState("");
  const [date, setDate] = useState(new Date().toISOString().split("T")[0]);

  const handleConfirm = () => {
    if (!staffName) {
      alert("Please enter staff name");
      return;
    }
    onConfirm({ staffName, date });
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: "20px",
          background: isDark
            ? "linear-gradient(135deg, rgba(15, 23, 42, 0.95) 0%, rgba(30, 41, 59, 0.95) 100%)"
            : "linear-gradient(135deg, rgba(255, 255, 255, 0.95) 0%, rgba(248, 250, 252, 0.95) 100%)",
          backdropFilter: "blur(30px)",
          border: `1px solid ${alpha(isDark ? "#475569" : "#e2e8f0", 0.3)}`,
        },
      }}
    >
      <DialogTitle sx={{ p: 3, pb: 2 }}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Box>
            <Typography variant="h5" sx={{ fontWeight: 700 }}>
              Report Lost Card
            </Typography>
            <Typography
              variant="body2"
              sx={{ color: "text.secondary", mt: 0.5 }}
            >
              {customerName} • Fine: ₹100
            </Typography>
          </Box>
          <IconButton onClick={onClose}>
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ p: 3 }}>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Paper
              sx={{
                p: 2,
                backgroundColor: alpha("#F59E0B", 0.1),
                borderRadius: "12px",
                border: `1px solid ${alpha("#F59E0B", 0.3)}`,
                display: "flex",
                alignItems: "center",
                gap: 2,
              }}
            >
              <WarningIcon sx={{ color: "#F59E0B", fontSize: 32 }} />
              <Box>
                <Typography
                  variant="subtitle2"
                  sx={{ fontWeight: 600, color: "#F59E0B" }}
                >
                  Card Lost Fine
                </Typography>
                <Typography variant="body2" sx={{ color: "text.secondary" }}>
                  A fine of ₹100 will be charged for issuing a new card.
                </Typography>
              </Box>
            </Paper>
          </Grid>

          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Date *"
              type="date"
              value={date}
              onChange={(e) => setDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
              InputProps={{
                startAdornment: (
                  <CalendarIcon sx={{ mr: 1, color: "text.secondary" }} />
                ),
              }}
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: "12px",
                },
              }}
            />
          </Grid>

          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Staff Name *"
              value={staffName}
              onChange={(e) => setStaffName(e.target.value)}
              placeholder="Enter staff name"
              InputProps={{
                startAdornment: (
                  <PersonIcon sx={{ mr: 1, color: "text.secondary" }} />
                ),
              }}
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: "12px",
                },
              }}
            />
          </Grid>
        </Grid>
      </DialogContent>

      <DialogActions sx={{ p: 3, pt: 0 }}>
        <Button
          fullWidth
          variant="outlined"
          onClick={onClose}
          sx={{
            borderRadius: "12px",
            py: 1.5,
            fontWeight: 600,
            borderColor: alpha(isDark ? "#475569" : "#e2e8f0", 0.5),
            color: isDark ? "#cbd5e1" : "#475569",
          }}
        >
          Cancel
        </Button>
        <Button
          fullWidth
          variant="contained"
          onClick={handleConfirm}
          sx={{
            borderRadius: "12px",
            py: 1.5,
            fontWeight: 600,
            background: "linear-gradient(135deg, #F59E0B 0%, #D97706 100%)",
            boxShadow: "0 8px 25px rgba(245, 158, 11, 0.3)",
            "&:hover": {
              transform: "translateY(-2px)",
              boxShadow: "0 12px 35px rgba(245, 158, 11, 0.4)",
            },
          }}
        >
          Confirm & Charge ₹100
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default function SchemeCardPage() {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const gridRef = useRef();
  const { customers, schemeCard, addSchemeCard, updateSchemeCard } =
    React.useContext(DataContext);

  // State
  const [schemeCustomers, setSchemeCustomers] = useState(schemeCard || []);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [viewDetailsOpen, setViewDetailsOpen] = useState(false);
  const [addTransactionOpen, setAddTransactionOpen] = useState(false);
  const [reportLostOpen, setReportLostOpen] = useState(false);
  const [selectedScheme, setSelectedScheme] = useState(null);
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  // Form states
  const [newScheme, setNewScheme] = useState({
    customerId: "",
    customerName: "",
    customerPhone: "",
    monthlyAmount: "",
    startDate: new Date().toISOString().split("T")[0],
  });

  useEffect(() => {
    // Show skeleton when component mounts (tab changes)
    setIsLoading(true);

    // Simulate loading delay (you can remove setTimeout if you're fetching data)
    const timer = setTimeout(() => {
      setIsLoading(false);
    }, 800);

    return () => clearTimeout(timer);
  }, []);

  const [newTransaction, setNewTransaction] = useState({
    month: null,
    amount: "",
    date: new Date().toISOString().split("T")[0],
    paymentMode: "Cash",
    staffName: "",
  });

  // Customer search input
  const [customerSearchInput, setCustomerSearchInput] = useState("");

  // Helper function to get customer details (similar to getCustomerName in TransactionHistory)
  const getCustomerDetails = useCallback(
    (customerId) => {
      return customers.find((c) => c.id === customerId) || null;
    },
    [customers]
  );

  // Filter customers based on search
  const filteredCustomers = useMemo(() => {
    if (!customers) return [];

    const searchTerm = customerSearchInput.toLowerCase().trim();
    if (!searchTerm) return customers;

    return customers.filter((customer) => {
      // Safely convert to strings and check if they exist
      const name = customer.name?.toLowerCase() || "";
      const contact = customer.contact?.toString().toLowerCase() || "";

      return name.includes(searchTerm) || contact.includes(searchTerm);
    });
  }, [customers, customerSearchInput]);

  // AG Grid Column Definitions - Updated to use customer data
  const columnDefs = useMemo(
    () => [
      {
        headerName: "Customer",
        field: "customerId", // Changed from customerName to customerId
        width: 200,
        cellRenderer: (params) => {
          const customer = getCustomerDetails(params.value);
          return (
            <Box>
              <Typography sx={{ fontWeight: 600, fontSize: "0.9rem" }}>
                {customer?.name || "N/A"}
              </Typography>
              <Typography variant="caption" sx={{ color: "text.secondary" }}>
                {customer?.contact || "N/A"}
              </Typography>
            </Box>
          );
        },
      },
      {
        headerName: "Card",
        field: "cardIssued",
        width: 100,
        cellRenderer: CardStatusCellRenderer,
      },
      {
        headerName: "Monthly Amount",
        field: "monthlyAmount",
        width: 140,
        cellRenderer: AmountCellRenderer,
      },
      {
        headerName: "Total Deposited",
        field: "totalDeposited",
        width: 150,
        cellRenderer: AmountCellRenderer,
      },
      {
        headerName: "Progress",
        field: "monthsCompleted",
        width: 180,
        cellRenderer: ProgressCellRenderer,
      },
      {
        headerName: "Maturity Amount",
        field: "maturityAmount",
        width: 160,
        cellRenderer: BonusCellRenderer,
      },
      {
        headerName: "Start Date",
        field: "startDate",
        width: 120,
        valueFormatter: (params) =>
          new Date(params.value).toLocaleDateString("en-IN", {
            day: "2-digit",
            month: "short",
            year: "numeric",
          }),
      },
      {
        headerName: "Status",
        field: "status",
        width: 120,
        cellRenderer: StatusCellRenderer,
      },
      {
        headerName: "Actions",
        field: "actions",
        width: 300,
        cellRenderer: ActionCellRenderer,
        cellRendererParams: {
          context: {
            onViewDetails: (data) => {
              // Enrich the data with customer details when viewing
              const enrichedData = {
                ...data,
                customerDetails: getCustomerDetails(data.customerId),
              };
              setSelectedScheme(enrichedData);
              setViewDetailsOpen(true);
            },
            onAddTransaction: (data) => {
              setSelectedScheme(data);
              // Find next pending month
              const nextPendingMonth = data.months?.find(
                (m) => m.status === "Pending"
              );
              if (nextPendingMonth) {
                setNewTransaction({
                  month: nextPendingMonth.month,
                  amount: nextPendingMonth.amount,
                  date: new Date().toISOString().split("T")[0],
                  paymentMode: "Cash",
                  staffName: "",
                });
              }
              setAddTransactionOpen(true);
            },
            onReportLostCard: (data) => {
              setSelectedScheme(data);
              setReportLostOpen(true);
            },
          },
        },
      },
    ],
    [getCustomerDetails]
  ); // Add customers to dependency array

  // Default column definitions
  const defaultColDef = useMemo(
    () => ({
      sortable: true,
      filter: true,
      resizable: true,
      suppressMenu: true,
    }),
    []
  );

  // Update schemeCustomers when schemeCard changes
  useEffect(() => {
    setSchemeCustomers(schemeCard || []);
  }, [schemeCard]);

  // Handlers
  const handleAddNewClick = () => {
    setDrawerOpen(true);
  };

  const handleDrawerClose = () => {
    setDrawerOpen(false);
    setNewScheme({
      customerId: "",
      customerName: "",
      customerPhone: "",
      monthlyAmount: "",
      startDate: new Date().toISOString().split("T")[0],
    });
    setCustomerSearchInput("");
    setTermsAccepted(false);
  };

  const handleAddTransactionClose = () => {
    setAddTransactionOpen(false);
    setSelectedScheme(null);
    setNewTransaction({
      month: null,
      amount: "",
      date: new Date().toISOString().split("T")[0],
      paymentMode: "Cash",
      staffName: "",
    });
  };

  const handleViewDetailsClose = () => {
    setViewDetailsOpen(false);
    setSelectedScheme(null);
  };

  const handleReportLostClose = () => {
    setReportLostOpen(false);
    setSelectedScheme(null);
  };

  const handleReportLostConfirm = ({ staffName, date }) => {
    // Update scheme with lost card fee
    const updatedScheme = {
      ...selectedScheme,
      cardLostFee: 100,
    };

    // Update in context
    updateSchemeCard(selectedScheme.id, updatedScheme);

    // Update in list
    setSchemeCustomers(
      schemeCustomers.map((s) =>
        s.id === selectedScheme.id ? updatedScheme : s
      )
    );

    handleReportLostClose();
  };

  const handleCustomerSelect = (event, value) => {
    if (value) {
      setNewScheme({
        ...newScheme,
        customerId: value.id,
        customerName: value.name,
        customerPhone: value.contact || value.phone,
      });
    } else {
      setNewScheme({
        ...newScheme,
        customerId: "",
        customerName: "",
        customerPhone: "",
      });
    }
  };

  const handleSubmitNewScheme = () => {
    // Validate
    if (
      !newScheme.customerId ||
      !newScheme.monthlyAmount ||
      !newScheme.startDate
    ) {
      alert("Please fill all required fields");
      return;
    }

    if (!termsAccepted) {
      alert("Please accept the terms and conditions");
      return;
    }

    // Find selected customer
    const customer = customers.find((c) => c.id === newScheme.customerId);
    const monthlyAmount = parseFloat(newScheme.monthlyAmount);

    // Generate months
    const months = generateMonths(newScheme.startDate, monthlyAmount);

    // Create new scheme (card issued free)
    const newSchemeData = {
      id: `SCHEME_${Date.now()}`,
      customerId: newScheme.customerId,
      customerName: customer.name,
      phone: customer.contact || customer.phone,
      email: customer.email,
      monthlyAmount: monthlyAmount,
      totalDeposited: 0,
      monthsCompleted: 0,
      startDate: newScheme.startDate,
      endDate: months[11].dueDate,
      cardIssued: true, // Card issued free at start
      cardLostFee: 0, // No lost card fee yet
      status: "Active",
      bonus: 1.5,
      months: months,
    };

    addSchemeCard(newSchemeData);
    handleDrawerClose();
  };

  const handleSubmitTransaction = () => {
    // Validate
    if (
      !newTransaction.amount ||
      !newTransaction.date ||
      !newTransaction.paymentMode ||
      !newTransaction.staffName
    ) {
      alert("Please fill all required fields");
      return;
    }

    // Update the month
    const updatedMonths = selectedScheme.months.map((m) => {
      if (m.month === newTransaction.month) {
        return {
          ...m,
          paidAmount: parseFloat(newTransaction.amount),
          status: "Paid",
          paymentDate: newTransaction.date,
          paymentMode: newTransaction.paymentMode,
          staffName: newTransaction.staffName,
        };
      }
      return m;
    });

    // Calculate new totals
    const totalDeposited = updatedMonths?.reduce(
      (sum, m) => sum + m.paidAmount,
      0
    );
    const monthsCompleted = updatedMonths?.filter(
      (m) => m.status === "Paid"
    ).length;
    const status = monthsCompleted === 12 ? "Completed" : "Active";

    // Update scheme
    const updatedScheme = {
      ...selectedScheme,
      months: updatedMonths,
      totalDeposited: totalDeposited,
      monthsCompleted: monthsCompleted,
      status: status,
    };

    // Update in context
    updateSchemeCard(selectedScheme.id, updatedScheme);

    // Update in list
    setSchemeCustomers(
      schemeCustomers.map((s) =>
        s.id === selectedScheme.id ? updatedScheme : s
      )
    );

    handleAddTransactionClose();
  };

  // Get next pending month for selected scheme
  const nextPendingMonth = selectedScheme?.months?.find(
    (m) => m.status === "Pending"
  );

  return (
    // ... (keep all the JSX the same, but update the view details dialog to use customer details)
    <Box
      sx={{
        minHeight: "100vh",
        m: 2,
        py: 4,
        px: { xs: 2, sm: 3, md: 3 },
        borderRadius: 2,
        background: isDark
          ? "linear-gradient(135deg, rgba(15, 23, 42, 0.3) 0%, rgba(30, 41, 59, 0.4) 100%)"
          : "linear-gradient(135deg, rgba(245, 247, 250, 0.3) 0%, rgba(228, 232, 240, 0.4) 100%)",
      }}
    >
      <Container maxWidth="xl">
        {/* Header */}
        <Box
          sx={{
            mb: 4,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Box>
            <Typography
              variant="h4"
              sx={{
                fontWeight: 800,
                mb: 0.5,
                color: "text.primary",
                fontSize: { xs: "1.75rem", md: "2rem" },
              }}
            >
              Gold Scheme Management
            </Typography>
            <Typography sx={{ color: "text.secondary", fontSize: "1.1rem" }}>
              12-month gold saving scheme with 1.5x bonus
            </Typography>
          </Box>

          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleAddNewClick}
            sx={{
              borderRadius: "12px",
              fontWeight: 600,
              px: 3,
              py: 1.5,
              background: "linear-gradient(135deg, #2563EB, #1D4ED8)",
              boxShadow: "0 8px 25px rgba(72, 75, 236, 0.3)",
              transition: "all 0.3s ease",
              "&:hover": {
                transform: "translateY(-2px)",
                boxShadow: "0 12px 35px rgba(72, 148, 236, 0.4)",
              },
            }}
          >
            Start New Scheme
          </Button>
        </Box>

        {/* Stats Cards */}
        {isLoading ? (
          <StatsSkeleton />
        ) : (
          <Grid container spacing={3} sx={{ mb: 4 }}>
            <Grid item xs={12} sm={6} md={3}>
              <Card
                sx={{
                  backgroundColor: isDark
                    ? "rgba(30, 41, 59, 0.4)"
                    : "rgba(255, 255, 255, 0.6)",
                  backdropFilter: "blur(20px)",
                  borderRadius: "20px",
                  border: `1px solid ${
                    isDark
                      ? "rgba(148, 163, 184, 0.1)"
                      : "rgba(255, 255, 255, 0.3)"
                  }`,
                }}
              >
                <CardContent>
                  <Typography
                    variant="caption"
                    sx={{ color: "text.secondary" }}
                  >
                    Active Schemes
                  </Typography>
                  <Typography
                    variant="h4"
                    sx={{ fontWeight: 700, color: "#484bec" }}
                  >
                    {
                      schemeCustomers?.filter((s) => s.status === "Active")
                        .length
                    }
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Card
                sx={{
                  backgroundColor: isDark
                    ? "rgba(30, 41, 59, 0.4)"
                    : "rgba(255, 255, 255, 0.6)",
                  backdropFilter: "blur(20px)",
                  borderRadius: "20px",
                  border: `1px solid ${
                    isDark
                      ? "rgba(148, 163, 184, 0.1)"
                      : "rgba(255, 255, 255, 0.3)"
                  }`,
                }}
              >
                <CardContent>
                  <Typography
                    variant="caption"
                    sx={{ color: "text.secondary" }}
                  >
                    Total Deposited
                  </Typography>
                  <Typography
                    variant="h4"
                    sx={{ fontWeight: 700, color: "#10B981" }}
                  >
                    ₹
                    {schemeCustomers
                      ?.reduce((sum, s) => sum + s.totalDeposited, 0)
                      .toLocaleString()}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Card
                sx={{
                  backgroundColor: isDark
                    ? "rgba(30, 41, 59, 0.4)"
                    : "rgba(255, 255, 255, 0.6)",
                  backdropFilter: "blur(20px)",
                  borderRadius: "20px",
                  border: `1px solid ${
                    isDark
                      ? "rgba(148, 163, 184, 0.1)"
                      : "rgba(255, 255, 255, 0.3)"
                  }`,
                }}
              >
                <CardContent>
                  <Typography
                    variant="caption"
                    sx={{ color: "text.secondary" }}
                  >
                    Completed Schemes
                  </Typography>
                  <Typography
                    variant="h4"
                    sx={{ fontWeight: 700, color: "#3B82F6" }}
                  >
                    {
                      schemeCustomers?.filter((s) => s.status === "Completed")
                        .length
                    }
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        )}

        {/* AG Grid Table */}
        {isLoading ? (
          <TableSkeleton />
        ) : (
          <Card
            sx={{
              backgroundColor: "transparent",
              borderRadius: "20px",
              overflow: "hidden",
              border: `1px solid ${
                isDark ? "rgba(148, 163, 184, 0.1)" : "rgba(0, 0, 0, 0.05)"
              }`,
            }}
          >
            <Box
              className={
                isDark ? "ag-theme-material-dark" : "ag-theme-material"
              }
              sx={{
                height: 500,
                width: "100%",
                "& .ag-root-wrapper": {
                  borderRadius: "20px",
                  border: "none",
                },
                "& .ag-header": {
                  backgroundColor: isDark
                    ? alpha("#1e293b", 0.8)
                    : alpha("#f8fafc", 0.8),
                  backdropFilter: "blur(10px)",
                  borderBottom: `1px solid ${
                    isDark ? "rgba(148, 163, 184, 0.1)" : "rgba(0, 0, 0, 0.05)"
                  }`,
                },
                "& .ag-header-cell": {
                  color: isDark ? "#e2e8f0" : "#1e293b",
                  fontWeight: 600,
                },
                "& .ag-row": {
                  backgroundColor: isDark
                    ? alpha("#1e293b", 0.4)
                    : alpha("#ffffff", 0.6),
                  backdropFilter: "blur(5px)",
                  borderBottom: `1px solid ${
                    isDark ? "rgba(148, 163, 184, 0.05)" : "rgba(0, 0, 0, 0.02)"
                  }`,
                  "&:hover": {
                    backgroundColor: isDark
                      ? alpha("#2d3a4f", 0.6)
                      : alpha("#ffffff", 0.8),
                  },
                },
                "& .ag-cell": {
                  display: "flex",
                  alignItems: "center",
                },
                "& .ag-paging-panel": {
                  borderTop: `1px solid ${
                    isDark ? "rgba(148, 163, 184, 0.1)" : "rgba(0, 0, 0, 0.05)"
                  }`,
                  backgroundColor: isDark
                    ? alpha("#1e293b", 0.4)
                    : alpha("#ffffff", 0.4),
                  backdropFilter: "blur(10px)",
                  color: isDark ? "#e2e8f0" : "#1e293b",
                },
              }}
            >
              <AgGridReact
                ref={gridRef}
                rowData={schemeCustomers}
                columnDefs={columnDefs}
                defaultColDef={defaultColDef}
                animateRows={true}
                rowSelection="single"
                pagination={true}
                paginationPageSize={10}
                domLayout="autoHeight"
                suppressCellFocus={true}
                enableCellTextSelection={true}
                rowHeight={60}
                headerHeight={50}
              />
            </Box>
          </Card>
        )}

        {/* Add New Scheme Drawer */}
        <Drawer
          anchor="right"
          open={drawerOpen}
          onClose={handleDrawerClose}
          sx={{
            zIndex: 1201,
          }}
          PaperProps={{
            sx: {
              width: { xs: "100%", sm: 550 },
              backgroundColor: isDark
                ? "rgba(15, 23, 42, 0.95)"
                : "rgba(255, 255, 255, 0.95)",
              backdropFilter: "blur(30px)",
              borderLeft: `1px solid ${alpha(
                isDark ? "#475569" : "#e2e8f0",
                0.3
              )}`,
              p: 3,
            },
          }}
        >
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mb: 3,
            }}
          >
            <Typography variant="h5" sx={{ fontWeight: 700 }}>
              Start New Gold Scheme
            </Typography>
            <IconButton onClick={handleDrawerClose}>
              <CloseIcon />
            </IconButton>
          </Box>

          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Autocomplete
                options={filteredCustomers}
                getOptionLabel={(option) => {
                  const phone = option.contact || option.phone || "";
                  return `${option.name || ""} ${
                    phone ? `- ${phone}` : ""
                  }`.trim();
                }}
                onChange={handleCustomerSelect}
                onInputChange={(event, newInputValue) => {
                  setCustomerSearchInput(newInputValue);
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Search Customer *"
                    placeholder="Type name or phone number..."
                    InputProps={{
                      ...params.InputProps,
                      startAdornment: (
                        <>
                          <SearchIcon sx={{ mr: 1, color: "text.secondary" }} />
                          {params.InputProps.startAdornment}
                        </>
                      ),
                    }}
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        borderRadius: "12px",
                      },
                    }}
                  />
                )}
                renderOption={(props, option) => (
                  <li {...props} key={option.id}>
                    <Box>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {option.name || "Unknown"}
                      </Typography>
                      <Typography
                        variant="caption"
                        sx={{ color: "text.secondary" }}
                      >
                        📞 {option.contact || option.phone || "N/A"} • ✉️{" "}
                        {option.email || "N/A"}
                      </Typography>
                    </Box>
                  </li>
                )}
                noOptionsText="No customers found"
                loadingText="Searching..."
                isOptionEqualToValue={(option, value) => option.id === value.id}
                filterOptions={(x) => x}
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Monthly Amount *"
                type="number"
                value={newScheme.monthlyAmount}
                onChange={(e) =>
                  setNewScheme({ ...newScheme, monthlyAmount: e.target.value })
                }
                InputProps={{
                  startAdornment: (
                    <MoneyIcon sx={{ mr: 1, color: "text.secondary" }} />
                  ),
                }}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: "12px",
                  },
                }}
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Start Date *"
                type="date"
                value={newScheme.startDate}
                onChange={(e) =>
                  setNewScheme({ ...newScheme, startDate: e.target.value })
                }
                InputLabelProps={{ shrink: true }}
                InputProps={{
                  startAdornment: (
                    <CalendarIcon sx={{ mr: 1, color: "text.secondary" }} />
                  ),
                }}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: "12px",
                  },
                }}
              />
            </Grid>

            <Grid item xs={12}>
              <Paper
                sx={{
                  p: 2,
                  backgroundColor: isDark
                    ? alpha("#1e293b", 0.5)
                    : alpha("#f8fafc", 0.8),
                  borderRadius: "12px",
                  border: `1px solid ${alpha(
                    isDark ? "#475569" : "#e2e8f0",
                    0.3
                  )}`,
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                  <CreditCardIcon sx={{ color: "#10B981" }} />
                  <Box>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      Free Card Issued
                    </Typography>
                    <Typography
                      variant="caption"
                      sx={{ color: "text.secondary" }}
                    >
                      First card is free. ₹100 fine if lost later.
                    </Typography>
                  </Box>
                </Box>
              </Paper>
            </Grid>

            <Grid item xs={12}>
              <TermsAndConditions
                accepted={termsAccepted}
                onAcceptChange={(e) => setTermsAccepted(e.target.checked)}
              />
            </Grid>

            <Grid item xs={12}>
              <Button
                fullWidth
                variant="contained"
                onClick={handleSubmitNewScheme}
                disabled={!termsAccepted}
                sx={{
                  borderRadius: "12px",
                  py: 1.5,
                  fontWeight: 600,
                  background: "linear-gradient(135deg, #2563EB, #1D4ED8)",
                  boxShadow: "0 8px 25px rgba(72, 138, 236, 0.3)",
                  "&:hover": {
                    transform: "translateY(-2px)",
                    boxShadow: "0 12px 35px rgba(72, 170, 236, 0.4)",
                  },
                  "&.Mui-disabled": {
                    background: isDark
                      ? alpha("#475569", 0.3)
                      : alpha("#cbd5e1", 0.3),
                  },
                }}
              >
                Start Scheme
              </Button>
            </Grid>
          </Grid>
        </Drawer>

        {/* View Details Dialog - Updated to use customer details */}
        <Dialog
          open={viewDetailsOpen}
          onClose={handleViewDetailsClose}
          maxWidth="md"
          fullWidth
          PaperProps={{
            sx: {
              borderRadius: "20px",
              background: isDark
                ? "linear-gradient(135deg, rgba(15, 23, 42, 0.95) 0%, rgba(30, 41, 59, 0.95) 100%)"
                : "linear-gradient(135deg, rgba(255, 255, 255, 0.95) 0%, rgba(248, 250, 252, 0.95) 100%)",
              backdropFilter: "blur(30px)",
              border: `1px solid ${alpha(isDark ? "#475569" : "#e2e8f0", 0.3)}`,
            },
          }}
        >
          {selectedScheme && (
            <>
              <DialogTitle sx={{ p: 3, pb: 2 }}>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}
                >
                  <Typography variant="h5" sx={{ fontWeight: 700 }}>
                    Scheme Details
                  </Typography>
                  <IconButton onClick={handleViewDetailsClose}>
                    <CloseIcon />
                  </IconButton>
                </Box>
              </DialogTitle>

              <DialogContent sx={{ p: 3 }}>
                {/* Customer Info - Using customer details */}
                <Paper
                  sx={{
                    p: 2,
                    mb: 3,
                    backgroundColor: isDark
                      ? alpha("#1e293b", 0.5)
                      : alpha("#f8fafc", 0.8),
                    borderRadius: "12px",
                  }}
                >
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                      <Box
                        sx={{ display: "flex", alignItems: "center", gap: 1 }}
                      >
                        <PersonIcon sx={{ color: "#EC4899" }} />
                        <Typography sx={{ fontWeight: 600 }}>
                          {selectedScheme.customerDetails?.name ||
                            selectedScheme.customerName}
                        </Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Box
                        sx={{ display: "flex", alignItems: "center", gap: 1 }}
                      >
                        <PhoneIcon sx={{ color: "#3B82F6" }} />
                        <Typography>
                          {selectedScheme.customerDetails?.contact ||
                            selectedScheme.phone}
                        </Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Box
                        sx={{ display: "flex", alignItems: "center", gap: 1 }}
                      >
                        <EmailIcon sx={{ color: "#EF4444" }} />
                        <Typography>
                          {selectedScheme.customerDetails?.email ||
                            selectedScheme.email}
                        </Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Box
                        sx={{ display: "flex", alignItems: "center", gap: 1 }}
                      >
                        <CreditCardIcon
                          sx={{
                            color:
                              selectedScheme.cardLostFee > 0
                                ? "#F59E0B"
                                : "#10B981",
                          }}
                        />
                        <Typography>
                          {selectedScheme.cardLostFee > 0
                            ? `Card lost (₹100 fine paid)`
                            : "Card issued free"}
                        </Typography>
                      </Box>
                    </Grid>
                  </Grid>
                </Paper>

                {/* Scheme Summary */}
                <Grid container spacing={2} sx={{ mb: 3 }}>
                  <Grid item xs={4}>
                    <Paper
                      sx={{
                        p: 2,
                        textAlign: "center",
                        backgroundColor: alpha("#10B981", 0.1),
                        borderRadius: "12px",
                      }}
                    >
                      <Typography
                        variant="caption"
                        sx={{ color: "text.secondary" }}
                      >
                        Monthly
                      </Typography>
                      <Typography
                        variant="h6"
                        sx={{ fontWeight: 700, color: "#10B981" }}
                      >
                        ₹{selectedScheme.monthlyAmount?.toLocaleString()}
                      </Typography>
                    </Paper>
                  </Grid>
                  <Grid item xs={4}>
                    <Paper
                      sx={{
                        p: 2,
                        textAlign: "center",
                        backgroundColor: alpha("#3B82F6", 0.1),
                        borderRadius: "12px",
                      }}
                    >
                      <Typography
                        variant="caption"
                        sx={{ color: "text.secondary" }}
                      >
                        Deposited
                      </Typography>
                      <Typography
                        variant="h6"
                        sx={{ fontWeight: 700, color: "#3B82F6" }}
                      >
                        ₹{selectedScheme.totalDeposited?.toLocaleString()}
                      </Typography>
                    </Paper>
                  </Grid>
                  <Grid item xs={4}>
                    <Paper
                      sx={{
                        p: 2,
                        textAlign: "center",
                        backgroundColor: alpha("#8B5CF6", 0.1),
                        borderRadius: "12px",
                      }}
                    >
                      <Typography
                        variant="caption"
                        sx={{ color: "text.secondary" }}
                      >
                        Maturity
                      </Typography>
                      <Typography
                        variant="h6"
                        sx={{ fontWeight: 700, color: "#8B5CF6" }}
                      >
                        ₹
                        {(
                          selectedScheme.monthlyAmount *
                          (12 + selectedScheme.bonus)
                        )?.toLocaleString()}
                      </Typography>
                    </Paper>
                  </Grid>
                </Grid>

                {/* Progress Bar */}
                <Box sx={{ mb: 3 }}>
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      mb: 1,
                    }}
                  >
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      Scheme Progress
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{ fontWeight: 600, color: "#EC4899" }}
                    >
                      {selectedScheme.monthsCompleted}/12 months
                    </Typography>
                  </Box>
                  <LinearProgress
                    variant="determinate"
                    value={(selectedScheme.monthsCompleted / 12) * 100}
                    sx={{
                      height: 8,
                      borderRadius: 4,
                      backgroundColor: isDark
                        ? alpha("#1e293b", 0.8)
                        : alpha("#e2e8f0", 0.8),
                      "& .MuiLinearProgress-bar": {
                        borderRadius: 4,
                        background:
                          "linear-gradient(90deg, #EC4899 0%, #DB2777 100%)",
                      },
                    }}
                  />
                </Box>

                {/* Month-wise Breakdown */}
                <Typography
                  variant="h6"
                  sx={{
                    fontWeight: 600,
                    mb: 2,
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                  }}
                >
                  <HistoryIcon /> Monthly Installments
                </Typography>

                <Box sx={{ maxHeight: 400, overflowY: "auto", pr: 1 }}>
                  {selectedScheme.months?.map((month) => (
                    <Card
                      key={month.month}
                      sx={{
                        mb: 2,
                        backgroundColor: isDark
                          ? alpha("#1e293b", 0.7)
                          : alpha("#ffffff", 0.7),
                        borderRadius: "12px",
                        border: `1px solid ${alpha(
                          month.status === "Paid" ? "#10B981" : "#F59E0B",
                          0.3
                        )}`,
                      }}
                    >
                      <CardContent>
                        <Grid container spacing={2} alignItems="center">
                          <Grid item xs={12} sm={1}>
                            <Typography
                              variant="h6"
                              sx={{ fontWeight: 700, color: "#EC4899" }}
                            >
                              #{month.month}
                            </Typography>
                          </Grid>
                          <Grid item xs={12} sm={2}>
                            <Typography
                              variant="caption"
                              sx={{ color: "text.secondary" }}
                            >
                              Due Date
                            </Typography>
                            <Typography sx={{ fontWeight: 500 }}>
                              {new Date(month.dueDate).toLocaleDateString()}
                            </Typography>
                          </Grid>
                          <Grid item xs={12} sm={2}>
                            <Typography
                              variant="caption"
                              sx={{ color: "text.secondary" }}
                            >
                              Amount
                            </Typography>
                            <Typography
                              sx={{ fontWeight: 700, color: "#10B981" }}
                            >
                              ₹{month.amount?.toLocaleString()}
                            </Typography>
                          </Grid>
                          <Grid item xs={12} sm={2}>
                            <Typography
                              variant="caption"
                              sx={{ color: "text.secondary" }}
                            >
                              Paid
                            </Typography>
                            <Typography sx={{ fontWeight: 600 }}>
                              {month.paidAmount > 0
                                ? `₹${month.paidAmount?.toLocaleString()}`
                                : "-"}
                            </Typography>
                          </Grid>
                          <Grid item xs={12} sm={2}>
                            <Typography
                              variant="caption"
                              sx={{ color: "text.secondary" }}
                            >
                              Status
                            </Typography>
                            <MonthStatusChip status={month.status} />
                          </Grid>
                          <Grid item xs={12} sm={3}>
                            {month.status === "Paid" && (
                              <>
                                <Typography
                                  variant="caption"
                                  sx={{ color: "text.secondary" }}
                                >
                                  {month.paymentMode} • {month.staffName}
                                </Typography>
                                <Typography
                                  variant="caption"
                                  display="block"
                                  sx={{ color: "text.secondary" }}
                                >
                                  {new Date(
                                    month.paymentDate
                                  ).toLocaleDateString()}
                                </Typography>
                              </>
                            )}
                          </Grid>
                        </Grid>
                      </CardContent>
                    </Card>
                  ))}
                </Box>
              </DialogContent>
            </>
          )}
        </Dialog>

        {/* Add Transaction Dialog */}
        <Dialog
          open={addTransactionOpen}
          onClose={handleAddTransactionClose}
          maxWidth="sm"
          fullWidth
          PaperProps={{
            sx: {
              borderRadius: "20px",
              background: isDark
                ? "linear-gradient(135deg, rgba(15, 23, 42, 0.95) 0%, rgba(30, 41, 59, 0.95) 100%)"
                : "linear-gradient(135deg, rgba(255, 255, 255, 0.95) 0%, rgba(248, 250, 252, 0.95) 100%)",
              backdropFilter: "blur(30px)",
              border: `1px solid ${alpha(isDark ? "#475569" : "#e2e8f0", 0.3)}`,
            },
          }}
        >
          {selectedScheme && nextPendingMonth && (
            <>
              <DialogTitle sx={{ p: 3, pb: 2 }}>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}
                >
                  <Box>
                    <Typography variant="h5" sx={{ fontWeight: 700 }}>
                      Pay Monthly Installment
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{ color: "text.secondary", mt: 0.5 }}
                    >
                      {selectedScheme.customerName} • Month #
                      {nextPendingMonth.month}
                    </Typography>
                  </Box>
                  <IconButton onClick={handleAddTransactionClose}>
                    <CloseIcon />
                  </IconButton>
                </Box>
              </DialogTitle>

              <DialogContent sx={{ p: 3 }}>
                <Grid container spacing={3}>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Amount *"
                      type="number"
                      value={newTransaction.amount}
                      onChange={(e) =>
                        setNewTransaction({
                          ...newTransaction,
                          amount: e.target.value,
                        })
                      }
                      InputProps={{
                        startAdornment: (
                          <MoneyIcon sx={{ mr: 1, color: "text.secondary" }} />
                        ),
                      }}
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          borderRadius: "12px",
                        },
                      }}
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Payment Date *"
                      type="date"
                      value={newTransaction.date}
                      onChange={(e) =>
                        setNewTransaction({
                          ...newTransaction,
                          date: e.target.value,
                        })
                      }
                      InputLabelProps={{ shrink: true }}
                      InputProps={{
                        startAdornment: (
                          <CalendarIcon
                            sx={{ mr: 1, color: "text.secondary" }}
                          />
                        ),
                      }}
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          borderRadius: "12px",
                        },
                      }}
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <FormControl fullWidth>
                      <InputLabel>Payment Mode *</InputLabel>
                      <Select
                        value={newTransaction.paymentMode}
                        onChange={(e) =>
                          setNewTransaction({
                            ...newTransaction,
                            paymentMode: e.target.value,
                          })
                        }
                        label="Payment Mode *"
                        sx={{ borderRadius: "12px" }}
                      >
                        <MenuItem value="Cash">Cash</MenuItem>
                        <MenuItem value="Online">Online</MenuItem>
                        <MenuItem value="Card">Card</MenuItem>
                        <MenuItem value="Cheque">Cheque</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>

                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Staff Name *"
                      value={newTransaction.staffName}
                      onChange={(e) =>
                        setNewTransaction({
                          ...newTransaction,
                          staffName: e.target.value,
                        })
                      }
                      placeholder="Enter staff name"
                      InputProps={{
                        startAdornment: (
                          <PersonIcon sx={{ mr: 1, color: "text.secondary" }} />
                        ),
                      }}
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          borderRadius: "12px",
                        },
                      }}
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <Paper
                      sx={{
                        p: 2,
                        backgroundColor: isDark
                          ? alpha("#1e293b", 0.5)
                          : alpha("#f8fafc", 0.8),
                        borderRadius: "12px",
                      }}
                    >
                      <Typography
                        variant="subtitle2"
                        sx={{ fontWeight: 600, mb: 1 }}
                      >
                        Payment Summary
                      </Typography>
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                          mb: 0.5,
                        }}
                      >
                        <Typography variant="body2">
                          Month #{nextPendingMonth.month}
                        </Typography>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          Due: ₹{nextPendingMonth.amount?.toLocaleString()}
                        </Typography>
                      </Box>
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                        }}
                      >
                        <Typography variant="body2">
                          Total Deposited After Payment
                        </Typography>
                        <Typography
                          variant="body2"
                          sx={{ fontWeight: 700, color: "#10B981" }}
                        >
                          ₹
                          {(
                            selectedScheme.totalDeposited +
                            parseFloat(newTransaction.amount || 0)
                          ).toLocaleString()}
                        </Typography>
                      </Box>
                    </Paper>
                  </Grid>
                </Grid>
              </DialogContent>

              <DialogActions sx={{ p: 3, pt: 0 }}>
                <Button
                  fullWidth
                  variant="outlined"
                  onClick={handleAddTransactionClose}
                  sx={{
                    borderRadius: "12px",
                    py: 1.5,
                    fontWeight: 600,
                    borderColor: alpha(isDark ? "#475569" : "#e2e8f0", 0.5),
                    color: isDark ? "#cbd5e1" : "#475569",
                  }}
                >
                  Cancel
                </Button>
                <Button
                  fullWidth
                  variant="contained"
                  onClick={handleSubmitTransaction}
                  sx={{
                    borderRadius: "12px",
                    py: 1.5,
                    fontWeight: 600,
                    background: "linear-gradient(135deg, #2563EB, #1D4ED8)",
                    boxShadow: "0 8px 25px rgba(72, 102, 236, 0.3)",
                    "&:hover": {
                      transform: "translateY(-2px)",
                      boxShadow: "0 12px 35px rgba(72, 102, 236, 0.4)",
                    },
                  }}
                >
                  Confirm Payment
                </Button>
              </DialogActions>
            </>
          )}
        </Dialog>

        {/* Report Lost Card Dialog */}
        <ReportLostCardDialog
          open={reportLostOpen}
          onClose={handleReportLostClose}
          onConfirm={handleReportLostConfirm}
          customerName={selectedScheme?.customerName}
          isDark={isDark}
        />
      </Container>
    </Box>
  );
}
